import * as THREE from 'three';
import { sourceToThree } from './awmesh.js';

export function calibratedLayerBox(layer, calibration) {
  const bounds = layer?.imageBounds;
  const matrix = layer?.transform?.worldToImage || calibration?.transform?.worldToImage;
  if (!bounds || !Array.isArray(matrix) || matrix.length !== 2
    || !matrix.every((row) => Array.isArray(row) && row.length === 3)) return null;
  const [a, b, c] = matrix[0].map(Number);
  const [d, e, f] = matrix[1].map(Number);
  const determinant = a * e - b * d;
  const imageValues = [bounds.xMin, bounds.yMin, bounds.xMax, bounds.yMax].map(Number);
  const zValues = [Number(layer.zMin), Number(layer.zMax)];
  if (![a, b, c, d, e, f, determinant, ...imageValues, ...zValues].every(Number.isFinite)
    || Math.abs(determinant) < 1e-12 || zValues[0] >= zValues[1]) return null;
  const box = new THREE.Box3();
  for (const imageX of [imageValues[0], imageValues[2]]) {
    for (const imageY of [imageValues[1], imageValues[3]]) {
      const shiftedX = imageX - c;
      const shiftedY = imageY - f;
      const sourceX = (shiftedX * e - b * shiftedY) / determinant;
      const sourceY = (a * shiftedY - shiftedX * d) / determinant;
      for (const sourceZ of zValues) {
        box.expandByPoint(sourceToThree({ x: sourceX, y: sourceY, z: sourceZ }));
      }
    }
  }
  return box.isEmpty() ? null : box;
}

export function fitDistanceForBox(
  box,
  camera,
  viewDirection,
  padding = 1.08,
  minimumDistance = 0,
) {
  if (!box?.isBox3 || box.isEmpty() || !camera?.isPerspectiveCamera) {
    throw new Error('CS2RADAR-CAMERA-101: a non-empty Box3 and PerspectiveCamera are required');
  }
  const direction = viewDirection.clone().normalize();
  if (!Number.isFinite(direction.lengthSq()) || direction.lengthSq() < 0.99) {
    throw new Error('CS2RADAR-CAMERA-102: view direction is invalid');
  }
  const aspect = Math.max(0.01, Number(camera.aspect) || 1);
  const verticalTangent = Math.tan(THREE.MathUtils.degToRad(camera.fov) * 0.5);
  const horizontalTangent = verticalTangent * aspect;
  const safePadding = Math.max(1, Number(padding) || 1);
  const center = box.getCenter(new THREE.Vector3());
  const right = new THREE.Vector3().crossVectors(camera.up, direction);
  if (right.lengthSq() < 1e-8) right.crossVectors(new THREE.Vector3(0, 0, 1), direction);
  right.normalize();
  const viewUp = new THREE.Vector3().crossVectors(direction, right).normalize();
  const corner = new THREE.Vector3();
  const offset = new THREE.Vector3();
  let distance = Math.max(0, Number(minimumDistance) || 0);
  for (const x of [box.min.x, box.max.x]) {
    for (const y of [box.min.y, box.max.y]) {
      for (const z of [box.min.z, box.max.z]) {
        corner.set(x, y, z);
        offset.copy(corner).sub(center);
        const depth = offset.dot(direction);
        const horizontal = Math.abs(offset.dot(right)) / horizontalTangent;
        const vertical = Math.abs(offset.dot(viewUp)) / verticalTangent;
        distance = Math.max(distance, depth + Math.max(horizontal, vertical) * safePadding);
      }
    }
  }
  return distance;
}
